"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';

const isList = [
    'Making your brand information easier to understand.',
    'Building genuine topical authority.',
    'Creating useful answers to complex questions.',
    'Demonstrating first-hand expertise.',
    'Improving technical accessibility.',
    'Strengthening the relationships between your content and entities.',
];

const isNotList = [
    'Stuffing AI-related keywords into pages.',
    'Publishing hundreds of AI-generated articles.',
    'Creating unnecessary pages for every possible question.',
    'Buying artificial mentions.',
    'Adding an llms.txt file and expecting AI visibility to magically appear.',
    'Promising guaranteed ChatGPT or Google AI citations.',
];

function CheckIcon() {
    return (
        <svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M2 5l2 2 4-4" stroke={PURPLE} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CrossIcon() {
    return (
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M2 2l6 6M8 2l-6 6" stroke="rgba(8,4,0,0.4)" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

function Row({ index, text, kind }: { index: number; text: string; kind: 'is' | 'isnt' }) {
    const isPositive = kind === 'is';
    return (
        <motion.div
            initial={{ opacity: 0, x: isPositive ? -14 : 14 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 + index * 0.06 }}
            className="group flex items-start gap-3 py-4"
            style={{ borderBottom: index < isList.length - 1 ? '1px dashed rgba(8,4,0,0.1)' : 'none' }}
        >
            <span
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110"
                style={{
                    background: isPositive ? 'rgba(55,48,163,0.1)' : 'rgba(8,4,0,0.04)',
                    border: `1px solid ${isPositive ? 'rgba(55,48,163,0.4)' : 'rgba(8,4,0,0.12)'}`,
                }}
            >
                {isPositive ? <CheckIcon /> : <CrossIcon />}
            </span>
            <span
                className="text-sm md:text-[15px] leading-relaxed"
                style={{ fontFamily: MANROPE, color: isPositive ? 'rgba(8,4,0,0.78)' : 'rgba(8,4,0,0.4)' }}
            >
                {text}
            </span>
        </motion.div>
    );
}

export default function AISEOWhatGEOIs() {
    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff' }}>
            {/* faint watermark */}
            <span
                className="absolute select-none pointer-events-none font-bold"
                style={{
                    top: '8%', left: '50%', transform: 'translateX(-50%)',
                    fontSize: 'clamp(120px, 18vw, 260px)', lineHeight: 1,
                    fontFamily: CLASH, color: 'rgba(8,4,0,0.03)', letterSpacing: '-0.02em',
                }}
            >
                GEO
            </span>

            <div className="max-w-[1160px] mx-auto relative z-10 flex flex-col gap-14">
                <div className="flex flex-col gap-4 items-center text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        Setting Expectations
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What GEO Is — And What It Isn&apos;t
                    </motion.h2>
                </div>

                {/* unified comparison panel */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative rounded-[32px] overflow-hidden"
                    style={{ border: '1px solid rgba(8,4,0,0.12)', background: '#fbfdfd' }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        {/* GEO IS */}
                        <div className="p-8 md:p-10 md:pr-14">
                            <span
                                className="inline-block text-xs font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                                style={{ color: PURPLE, fontFamily: CLASH, border: `1.5px solid ${PURPLE}`, background: 'rgba(55,48,163,0.08)', transform: 'rotate(-1.5deg)' }}
                            >
                                Geo is
                            </span>
                            <div className="flex flex-col mt-6">
                                {isList.map((item, i) => (
                                    <Row key={item} index={i} text={item} kind="is" />
                                ))}
                            </div>
                        </div>

                        {/* GEO ISN'T */}
                        <div
                            className="p-8 md:p-10 md:pl-14"
                            style={{ borderTop: '1px dashed rgba(8,4,0,0.1)' }}
                        >
                            <span
                                className="inline-block text-xs font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                                style={{ color: 'rgba(8,4,0,0.5)', fontFamily: CLASH, border: '1.5px solid rgba(8,4,0,0.18)', transform: 'rotate(1.5deg)' }}
                            >
                                Geo isn&apos;t
                            </span>
                            <div className="flex flex-col mt-6">
                                {isNotList.map((item, i) => (
                                    <Row key={item} index={i} text={item} kind="isnt" />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* center divider + badge, desktop only */}
                    <div
                        className="hidden md:block absolute top-0 bottom-0 pointer-events-none"
                        style={{ left: '50%', width: 1, background: 'rgba(8,4,0,0.1)', transform: 'translateX(-50%)' }}
                    />
                    <div
                        className="hidden md:flex absolute items-center justify-center pointer-events-none"
                        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                        {/* pulsing glow, sits behind the badge */}
                        <motion.span
                            className="absolute rounded-full"
                            style={{ width: 52, height: 52, background: PURPLE, filter: 'blur(14px)' }}
                            animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.25, 1] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                        />

                        {/* entrance */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.7 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.4, ease: 'backOut' }}
                        >
                            {/* continuous pulse */}
                            <motion.div
                                className="relative flex items-center justify-center rounded-full"
                                style={{
                                    width: 52, height: 52,
                                    background: INK, border: `1.5px solid ${PURPLE}`,
                                    boxShadow: '0 0 0 6px #fbfdfd',
                                }}
                                animate={{ scale: [1, 1.06, 1] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                <span className="text-[11px] font-black tracking-wider" style={{ fontFamily: CLASH, color: '#ffffff' }}>VS</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* verdict strip */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex gap-5 rounded-2xl px-6 py-5 md:px-8 md:py-6"
                    style={{ background: 'rgba(55,48,163,0.05)', borderLeft: `3px solid ${PURPLE}` }}
                >
                    <span className="text-xs font-bold tracking-[0.18em] uppercase shrink-0 hidden sm:block" style={{ fontFamily: CLASH, color: PURPLE }}>
                        Verdict
                    </span>
                    <p
                        className="text-sm md:text-base leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}
                    >
                        Google&apos;s current guidance explicitly says there are no special requirements for AI Overviews/AI Mode beyond normal Search eligibility and foundational SEO, and says tactics such as unnecessary content creation and artificial mentions aren&apos;t effective long-term strategies.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
